"use client";

import { ArrowLeft, ArrowRight, X } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	useCarousel,
} from "@/components/ui/carousel";

interface ResultReport {
	title: string;
	thumbnailSrc: string;
	fullSrc: string;
	width: number;
	height: number;
}

const reports: ResultReport[] = [
	{
		title: "Folk Rakaat — 4.33M Impressions",
		thumbnailSrc: "/home/analytics/thumbnails/folk-rakaat-analytics.webp",
		fullSrc: "/home/analytics/folk-rakaat-analytics.png",
		width: 960,
		height: 540,
	},
	{
		title: "Folk Rakaat Campaign",
		thumbnailSrc: "/home/analytics/thumbnails/folk-rakaat-list.webp",
		fullSrc: "/home/analytics/folk-rakaat-list.png",
		width: 960,
		height: 540,
	},
	{
		title: "Arabic Client — 1.7M Impressions",
		thumbnailSrc: "/home/analytics/thumbnails/arabic-client-analytics.webp",
		fullSrc: "/home/analytics/arabic-client-analytics.png",
		width: 960,
		height: 540,
	},
	{
		title: "Arabic Client Campaign",
		thumbnailSrc: "/home/analytics/thumbnails/arabic-client-list.webp",
		fullSrc: "/home/analytics/arabic-client-list.png",
		width: 960,
		height: 540,
	},
	{
		title: "Naa Chalo — 1.58M Impressions",
		thumbnailSrc: "/home/analytics/thumbnails/naa-chalo-analytics.webp",
		fullSrc: "/home/analytics/naa-chalo-analytics.png",
		width: 960,
		height: 540,
	},
	{
		title: "Naa Chalo Campaign",
		thumbnailSrc: "/home/analytics/thumbnails/naa-chalo-list.webp",
		fullSrc: "/home/analytics/naa-chalo-list.png",
		width: 960,
		height: 540,
	},
	{
		title: "Tulip Official — 858K Impressions",
		thumbnailSrc: "/home/analytics/thumbnails/tulip-official-analytics.webp",
		fullSrc: "/home/analytics/tulip-official-analytics.png",
		width: 960,
		height: 540,
	},
	{
		title: "Tulip Official Campaign",
		thumbnailSrc: "/home/analytics/thumbnails/tulip-official-list.webp",
		fullSrc: "/home/analytics/tulip-official-list.png",
		width: 960,
		height: 540,
	},
	{
		title: "Yar Dhara — 595K Impressions",
		thumbnailSrc: "/home/analytics/thumbnails/yar-dhara-analytics.webp",
		fullSrc: "/home/analytics/yar-dhara-analytics.png",
		width: 960,
		height: 540,
	},
	{
		title: "Yar Dhara Campaign",
		thumbnailSrc: "/home/analytics/thumbnails/yar-dhara-list.webp",
		fullSrc: "/home/analytics/yar-dhara-list.png",
		width: 960,
		height: 540,
	},
	{
		title: "The Bridal — 368K Impressions",
		thumbnailSrc: "/home/analytics/thumbnails/bridal-mashup-analytics.webp",
		fullSrc: "/home/analytics/bridal-mashup-analytics.png",
		width: 960,
		height: 540,
	},
	{
		title: "The Bridal Campaign",
		thumbnailSrc: "/home/analytics/thumbnails/bridal-mashup-list.webp",
		fullSrc: "/home/analytics/bridal-mashup-list.png",
		width: 960,
		height: 540,
	},
];

export default function Metrics() {
	const [selectedReport, setSelectedReport] = useState<ResultReport | null>(
		null,
	);

	return (
		<section className="py-24 bg-[#dc2626] text-white overflow-hidden">
			<div className="container mx-auto px-4 md:px-6">
				<div className="mb-12 text-center">
					<h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter mb-2">
						Our <span className="text-black">Results</span>
					</h2>
					<p className="text-red-100 text-lg font-medium">Our past results</p>
				</div>

				<Carousel
					opts={{
						align: "start",
						loop: false,
						dragFree: true,
						containScroll: "trimSnaps",
					}}
					className="w-full overscroll-x-contain"
				>
					<div className="overflow-visible">
						<CarouselContent className="-ml-6 pb-12 pt-4">
							{reports.map((report) => (
								<CarouselItem key={report.fullSrc} className="pl-6 basis-auto">
									<button
										type="button"
										onClick={() => setSelectedReport(report)}
										className="relative aspect-video w-[min(86vw,520px)] md:w-[42rem] rounded-2xl overflow-hidden cursor-pointer group shadow-2xl border-4 border-white/10 hover:border-white/30 transition-colors duration-300 animate-on-scroll-view text-left"
										aria-label={`Open ${report.title}`}
									>
										<Image
											src={report.thumbnailSrc}
											alt={report.title}
											width={report.width}
											height={report.height}
											className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
											loading="lazy"
											sizes="(max-width: 768px) 86vw, 672px"
										/>

										<div className="absolute inset-0 bg-linear-to-t from-black/90 via-black/15 to-transparent opacity-70 transition-opacity duration-300 group-hover:opacity-90 md:opacity-80 md:group-hover:opacity-100" />

										<div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
											<h3 className="text-xl md:text-2xl font-black uppercase tracking-tighter text-white leading-none">
												{report.title}
											</h3>
											<div className="h-1 w-12 bg-[#ef4444] mt-3 rounded-full" />
										</div>
									</button>
								</CarouselItem>
							))}
							<CarouselItem
								className="pl-6 basis-[100px] md:basis-[200px] pointer-events-none select-none"
								aria-hidden="true"
							>
								<div className="h-full" />
							</CarouselItem>
						</CarouselContent>
					</div>

					<div className="mt-8 flex justify-end gap-4">
						<CarouselArrows />
					</div>
				</Carousel>
			</div>

			{selectedReport && (
				<div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4">
					<button
						type="button"
						className="absolute inset-0 cursor-zoom-out"
						aria-label="Close enlarged image"
						onClick={() => setSelectedReport(null)}
					/>
					<div className="relative max-w-[90vw] max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
						<button
							type="button"
							onClick={() => setSelectedReport(null)}
							className="absolute -top-12 right-0 text-white/50 hover:text-white transition-colors"
							aria-label="Close enlarged image"
						>
							<X className="w-8 h-8" />
						</button>
						<Image
							src={selectedReport.fullSrc}
							alt={`Full view of ${selectedReport.title}`}
							width={1920}
							height={1080}
							className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl border border-white/10"
							sizes="90vw"
						/>
					</div>
				</div>
			)}
		</section>
	);
}

function CarouselArrows() {
	const { scrollPrev, scrollNext, canScrollPrev, canScrollNext } =
		useCarousel();

	return (
		<div className="flex items-center gap-4">
			<button
				type="button"
				onClick={scrollPrev}
				disabled={!canScrollPrev}
				aria-label="Previous slide"
				className="h-12 w-12 rounded-full border-2 border-white/20 bg-white/10 text-white flex items-center justify-center hover:bg-white hover:text-[#dc2626] hover:border-white transition-[color,background-color,border-color,opacity] duration-300 disabled:opacity-30 disabled:cursor-not-allowed backdrop-blur-sm"
			>
				<ArrowLeft className="w-6 h-6" />
			</button>
			<button
				type="button"
				onClick={scrollNext}
				disabled={!canScrollNext}
				aria-label="Next slide"
				className="h-12 w-12 rounded-full border-2 border-white/20 bg-white/10 text-white flex items-center justify-center hover:bg-white hover:text-[#dc2626] hover:border-white transition-[color,background-color,border-color,opacity] duration-300 disabled:opacity-30 disabled:cursor-not-allowed backdrop-blur-sm"
			>
				<ArrowRight className="w-6 h-6" />
			</button>
		</div>
	);
}
